import { Link } from 'react-router-dom';
import type { Article } from '@/types';
import { formatHeat } from '@/utils/format';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'list';
}

const MetaRow = ({ article, dense = false }: { article: Article; dense?: boolean }) => (
  <div
    className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-foreground-500 ${
      dense ? 'text-[11px]' : 'text-xs md:text-sm'
    }`}
  >
    <span className="inline-flex items-center gap-1">
      <i className="ri-user-line text-[13px]"></i>
      {article.author}
    </span>
    <span className="hidden sm:inline-flex items-center gap-1">
      <i className="ri-time-line text-[13px]"></i>
      {article.readTime}
    </span>
    <span className="inline-flex items-center gap-1 text-primary-600">
      <i className="ri-fire-line text-[13px]"></i>
      {formatHeat(article.heat)}
    </span>
  </div>
);

const ArticleCard = ({ article, variant = 'grid' }: ArticleCardProps) => {
  if (variant === 'list') {
    return (
      <article className="group relative flex gap-4 rounded-lg border border-background-200/70 bg-background-100/60 p-3 transition-colors duration-200 hover:border-primary-300 hover:bg-background-100 md:p-4">
        <Link
          to={`/article/${article.id}`}
          className="relative block h-24 w-28 shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-40"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={`${article.title} - 看瓜爆料`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                {article.topicName}
              </span>
              {article.isHot && (
                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] text-primary-700">
                  独家首发
                </span>
              )}
            </div>
            <h3 className="font-heading text-sm font-bold leading-snug text-foreground-950 md:text-base">
              <Link to={`/article/${article.id}`} className="hover:text-primary-600">
                {article.title}
              </Link>
            </h3>
            <p className="mt-1.5 line-clamp-2 hidden text-xs text-foreground-600 sm:block md:text-sm">
              {article.excerpt}
            </p>
          </div>
          <div className="mt-2">
            <MetaRow article={article} dense />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100/60 transition-colors duration-200 hover:border-primary-300">
      <span className="news-card-line h-1 w-full" aria-hidden="true"></span>
      <Link
        to={`/article/${article.id}`}
        className="relative block h-44 w-full overflow-hidden sm:h-48"
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={`${article.title} - 看瓜爆料`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/75 px-2.5 py-1 text-[11px] text-background-50 backdrop-blur">
          {article.topicName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/article/${article.id}`} className="hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600">{article.excerpt}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3">
          <MetaRow article={article} dense />
          <span className="hidden items-center gap-1 text-xs text-foreground-500 sm:inline-flex">
            {article.dateLabel}
          </span>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;